import React, { useState } from 'react'
import { Menu, MenuItem, Card, Position } from '@blueprintjs/core'
import { Button, Flex, Popover } from '@ui'
import LocationSearch from './Search'
import useLocations from './useLocations'
import { IUIButtonProps } from '@ui/buttons/Button'
import { CityColumn, Building, Floor } from './helperComponents'
import { get, uniqBy, without } from 'lodash'
import { Mode } from './types'
import { IBuildingDetails, IFloorDetails, IPortfolioCity } from '@api'
import css from './locationSelect.module.scss'

interface MultiFloorSelectProps {
  values: IFloorDetails[]
  buttonProps?: IUIButtonProps
  onChange: (value: any) => void
  includeOldBuildings?: boolean
}

/**
 * This is a helper component which should not be used directly.
 * Use @ui/selectes/MultiLocationSelect instead.
 */
export default function MultiFloorSelect({
  values,
  buttonProps = {},
  onChange,
  includeOldBuildings,
}: MultiFloorSelectProps) {
  const regions = useLocations(includeOldBuildings)
  const [cityNav, setCityNav] = useState<IPortfolioCity | null>(null)
  const buildings: IBuildingDetails[] = get(cityNav, 'buildings', [])

  return (
    <Popover
      minimal
      usePortal={true}
      position={Position.BOTTOM}
      modifiers={{ arrow: { enabled: false } }}
      content={
        <div className={css.container}>
          <LocationSearch
            mode={Mode.MULTI_FLOOR}
            regions={regions}
            selectCity={setCityNav}
            selectBuilding={() => null}
            selectFloor={(floor) =>
              onChange(values.includes(floor) ? without(values, floor) : [...values, floor])
            }
          />
          <Flex>
            <CityColumn regions={regions} cityNav={cityNav} setCityNav={setCityNav} />
            <div className={css.col} style={{ minWidth: 300 }}>
              <div className={css.col_header} style={{ paddingLeft: 11 }}>
                buildings / floors
              </div>
              {buildings.length ? (
                <div className={css.list}>
                  <Menu>
                    {buildings.map((b) => (
                      <Card
                        key={b.id}
                        style={{
                          padding: 10,
                          marginBottom: 10,
                          overflow: 'hidden',
                        }}
                      >
                        <Menu style={{ margin: '0 -11px', marginBottom: -10 }}>
                          <MenuItem
                            shouldDismissPopover={false}
                            disabled={b.floors?.length === 0}
                            onClick={(e: any) => {
                              e.preventDefault()
                              if (allFloorsSelected(b, values)) {
                                onChange(without(values, ...(b.floors || [])))
                              } else {
                                onChange(uniqBy([...values, ...(b.floors || [])], 'id'))
                              }
                            }}
                            text={
                              <Building
                                mode={Mode.MULTI_FLOOR}
                                building={b}
                                selected={allFloorsSelected(b, values)}
                              />
                            }
                          />
                        </Menu>
                        <Menu style={{ margin: '0 -11px' }}>
                          {b.floors && b.floors.length ? (
                            b.floors.map((f) => (
                              <MenuItem
                                key={f.id}
                                shouldDismissPopover={false}
                                onClick={(e) => {
                                  e.preventDefault()
                                  onChange(values.includes(f) ? without(values, f) : [...values, f])
                                }}
                                text={
                                  <Floor
                                    mode={Mode.MULTI_FLOOR}
                                    floor={f}
                                    selected={values.includes(f)}
                                  />
                                }
                              />
                            ))
                          ) : (
                            <MenuItem disabled text="No floors" />
                          )}
                        </Menu>
                      </Card>
                    ))}
                  </Menu>
                </div>
              ) : (
                <Menu>
                  <MenuItem disabled text="No buildings" />
                </Menu>
              )}
            </div>
          </Flex>
        </div>
      }
    >
      <Button {...buttonProps} />
    </Popover>
  )
}

function allFloorsSelected(building: IBuildingDetails, selection: IFloorDetails[]) {
  let allSelected = true

  if (!building.floors?.length) {
    return false
  }

  building.floors.forEach((f) => {
    if (!selection.includes(f)) {
      allSelected = false
    }
  })

  return allSelected
}
